// Phase 23B — Outlet config (admin toggle for require_storefront_photo)
// Phase 26F.1 — Retailer export now supports Distributor / Zone / Sub-Zone
// multi-select filters. The Download button includes only rows matching
// the active filter set (visibility scoping still applied server-side).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Camera, Download, Filter, X, ChevronDown } from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

function MultiPicker({ label, options, selectedIds, setSelectedIds, testId }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => options.filter(o => (o.label || "").toLowerCase().includes(q.toLowerCase())),
    [options, q],
  );
  const toggle = (id) => {
    setSelectedIds(selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id]);
  };
  const clear = (e) => { e.stopPropagation(); setSelectedIds([]); };
  const count = selectedIds.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50"
        >
          <Filter className="h-3 w-3" />
          <span>{label}</span>
          {count > 0 && (
            <span className="rounded-full bg-[#6D2C91] px-1.5 py-0 text-[10px] font-semibold text-white"
                    data-testid={`${testId}-count`}>{count}</span>
          )}
          {count > 0 ? (
            <span onClick={clear} className="ml-1 rounded p-0.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                    data-testid={`${testId}-clear`}>
              <X className="h-3 w-3" />
            </span>
          ) : (
            <ChevronDown className="h-3 w-3 text-neutral-500" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}…`}
          className="h-8 text-xs mb-2"
          data-testid={`${testId}-search`}
        />
        <div className="max-h-56 overflow-y-auto space-y-1" data-testid={`${testId}-options`}>
          {filtered.length === 0 && (
            <div className="text-center text-xs text-neutral-400 py-4">No matches</div>
          )}
          {filtered.map(o => (
            <label key={o.id}
                    className="flex items-center gap-2 rounded px-2 py-1 hover:bg-neutral-50 cursor-pointer"
                    data-testid={`${testId}-option-${o.id}`}>
              <Checkbox
                checked={selectedIds.includes(o.id)}
                onCheckedChange={() => toggle(o.id)}
              />
              <span className="text-xs text-neutral-800">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function OutletConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Phase 26F.1 — filter state
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [selZoneIds, setSelZoneIds] = useState([]);
  const [selSubZoneIds, setSelSubZoneIds] = useState([]);
  const [selDistIds, setSelDistIds] = useState([]);

  useEffect(() => {
    api.get("/outlet-config")
      .then(r => setCfg(r.data))
      .catch(e => toast.error(formatApiError(e)));
    api.get("/zones").then(r => setZones(
      (r.data.items || r.data || []).map(z => ({ id: z.id, label: `${z.code || ""} · ${z.name}` }))
    )).catch(() => {});
    api.get("/masters/sub-zones", { params: { page_size: 100 } }).then(r => setSubZones(
      (r.data.items || []).map(z => ({ id: z.id, label: z.name }))
    )).catch(() => {});
    api.get("/distributors", { params: { page_size: 2000 } }).then(r => setDistributors(
      (r.data.items || []).map(d => ({ id: d.id, label: `${d.code || ""} · ${d.name}` }))
    )).catch(() => {});
  }, []);

  const toggle = async (v) => {
    setSaving(true);
    try {
      const r = await api.put("/outlet-config", { require_storefront_photo: v });
      setCfg(r.data);
      toast.success(`Storefront photo ${v ? "required" : "optional"} for new outlets`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const activeFilterCount = selZoneIds.length + selSubZoneIds.length + selDistIds.length;

  const clearAll = () => {
    setSelZoneIds([]); setSelSubZoneIds([]); setSelDistIds([]);
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: "xlsx" });
      if (selZoneIds.length) params.set("zone_ids", selZoneIds.join(","));
      if (selSubZoneIds.length) params.set("sub_zone_ids", selSubZoneIds.join(","));
      if (selDistIds.length) params.set("distributor_ids", selDistIds.join(","));
      const r = await fetch(`${API_BASE}/retailers/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 13);
      a.href = u; a.download = `retailers_outlets_export_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success(activeFilterCount > 0
        ? `Retailers exported (${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} applied)`
        : "Retailers exported");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="outlet-config-page">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Outlet Configuration</h1>
        <p className="text-xs text-neutral-500 mt-0.5">Policy toggles + master-data export.</p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-[#6D2C91]/10 text-[#6D2C91] p-2">
              <Camera className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-neutral-900">Require storefront photo</div>
              <div className="text-xs text-neutral-500 mt-0.5 max-w-xl">
                When enabled, the SE Add-Outlet flow must include a storefront photo
                (photo + GPS + timestamp). Existing outlets are unaffected.
              </div>
            </div>
            {cfg ? (
              <Switch
                checked={cfg.require_storefront_photo}
                onCheckedChange={toggle}
                disabled={saving}
                data-testid="outlet-config-storefront-toggle"
              />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
            )}
          </div>
          {cfg?.updated_at && (
            <div className="text-[10px] text-neutral-400 mt-2 pl-12" data-testid="outlet-config-updated-at">
              Last updated {new Date(cfg.updated_at).toLocaleString()}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-emerald-100 text-emerald-700 p-2">
              <Download className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-neutral-900">Export retailers/outlets</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Streaming XLSX — safe on 20k+ rows. Visibility-scoped.
              </div>

              {/* Phase 26F.1 filter bar */}
              <div className="mt-3 flex flex-wrap items-center gap-2"
                     data-testid="outlet-config-filter-bar">
                <MultiPicker
                  label="Distributor"
                  options={distributors}
                  selectedIds={selDistIds}
                  setSelectedIds={setSelDistIds}
                  testId="filter-distributors"
                />
                <MultiPicker
                  label="Zone"
                  options={zones}
                  selectedIds={selZoneIds}
                  setSelectedIds={setSelZoneIds}
                  testId="filter-zones"
                />
                <MultiPicker
                  label="Sub-Zone"
                  options={subZones}
                  selectedIds={selSubZoneIds}
                  setSelectedIds={setSelSubZoneIds}
                  testId="filter-sub-zones"
                />
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs text-neutral-500 underline underline-offset-2 hover:text-neutral-800"
                    data-testid="outlet-config-clear-filters"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <Button onClick={doExport} disabled={exporting}
                    className="bg-[#6D2C91] text-white hover:bg-[#5b2478] shrink-0"
                    data-testid="outlet-config-export-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Download{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
